import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type RefObject } from "react";
import { useForm } from "react-hook-form";
import {
  giftCardAdminPurchaseSessionInputSchema,
  giftCardAdjustmentInputSchema,
  giftCardStorefrontSettingsSchema,
  giftCardThemeInputSchema,
  type GiftCardStorefrontSettings,
  type GiftCardTheme,
  type GiftCardThemeSnapshot
} from "@booking-seconds/types";
import { formatCurrency } from "@booking-seconds/utils";
import { Badge, Button, Card, Field, SectionHeader } from "@booking-seconds/ui";
import { GiftCardThemePreview } from "./gift-card-theme-preview";
import { apiFetch } from "./lib/api";
import { StripePaymentCard, type StripePaymentCardHandle, type StripePaymentSession } from "./stripe-payment-card";

type GiftCardListItem = {
  id: string;
  code: string;
  originalAmount: number;
  balanceAmount: number;
  status: string;
  createdAt: string;
  recipientName: string;
  recipientEmail: string;
  purchaserName: string;
  purchaserEmail: string;
  updatedAt: string;
  orderId?: string | null;
  orderStatus?: string | null;
  source?: string | null;
  paymentMode?: string | null;
  giftCardThemeId?: string;
  giftCardTheme?: GiftCardThemeSnapshot | null;
};

type GiftCardOrderItem = {
  id: string;
  amount: number;
  status: string;
  publicStatus?: string;
  purchaserName: string;
  purchaserEmail: string;
  recipientName: string;
  recipientEmail: string;
  deliveredAt: string;
  fulfilledAt: string;
  createdAt: string;
  scheduledFor?: string;
  schedulingPolicy?: string;
  buyerRevealPolicy?: string;
  deliverySkipped?: boolean;
  revealAvailable?: boolean;
  giftCardId?: string | null;
  giftCardCode: string;
  source?: string | null;
  paymentMode?: string | null;
  internalNote?: string | null;
  providerIntentId?: string | null;
  giftCardThemeId?: string;
  giftCardTheme?: GiftCardThemeSnapshot | null;
};

type GiftCardTransaction = {
  id: string;
  amount: number;
  transactionType: string;
  note: string;
  bookingReference?: string | null;
  createdAt: string;
  config?: Record<string, unknown>;
};

type GiftCardRedemptionItem = {
  id: string;
  amountRedeemed: number;
  source: string;
  customerName: string;
  bookingReference?: string | null;
  bookingTotal: number;
  serviceDate?: string | null;
  note: string;
  createdAt: string;
};

type GiftCardDetail = GiftCardListItem & {
  giftMessage?: string;
  deliveredAt?: string;
  orderStatus?: string;
  orderCreatedAt?: string;
  scheduledFor?: string;
  source?: string;
  paymentMode?: string;
  internalNote?: string;
  createdByUserId?: string;
  transactions: GiftCardTransaction[];
};

type GiftCardLegacyImportPreview = {
  summary: {
    cards: number;
    redemptions: number;
    changes: number;
    createCount: number;
    updateCount: number;
    errorCount: number;
  };
  items: Array<{
    code: string;
    recipientName: string;
    purchaserName: string;
    originalAmount: number;
    currentBalance: number;
    paymentMode: string;
    status: string;
    issuedAt: string;
    redemptionCount: number;
    changeCount: number;
    syntheticRedeemedAmount: number;
    action: "create" | "update";
  }>;
  errors: string[];
};

type GiftCardLegacyImportResult = {
  imported: number;
  created: number;
  updated: number;
  importedTransactions: number;
  emailed: number;
  ids: string[];
};

type AdminGiftCardPurchaseSession = StripePaymentSession & {
  orderId: string;
  giftCardThemeId: string;
  giftCardTheme: GiftCardThemeSnapshot;
};

type GiftCardOrderDetail = GiftCardOrderItem & {
  purchaserName: string;
  purchaserEmail: string;
  recipientName: string;
  recipientEmail: string;
};

type GiftCardStorefrontDraft = GiftCardStorefrontSettings & {
  presetAmountsText: string;
};

type AdminGiftCardIssueSuccess = {
  paymentMode: "card" | "free";
  orderId: string;
  giftCardId: string | null;
  giftCardCode: string;
  giftCardTheme: GiftCardThemeSnapshot | null;
};

function formatDateTime(value: string | null | undefined) {
  if (!value) {
    return "Not yet";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

function formatOrderSourceLabel(value: string | null | undefined) {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (normalized === "admin") {
    return "Admin";
  }
  if (normalized === "legacy_import") {
    return "Legacy import";
  }
  return "Public";
}

function formatPaymentModeLabel(value: string | null | undefined) {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (normalized === "free") {
    return "Free";
  }
  if (normalized === "legacy") {
    return "Legacy";
  }
  return "Card";
}

const LEGACY_GIFT_CARD_TEMPLATE_HEADERS = [
  "code",
  "original_amount",
  "current_balance",
  "issued_at",
  "purchaser_name",
  "purchaser_email",
  "recipient_name",
  "recipient_email",
  "message",
  "payment_mode",
  "status",
  "external_order_id",
  "external_transaction_id",
  "external_receipt_url",
  "notes"
];

const LEGACY_GIFT_CARD_REDEMPTIONS_TEMPLATE_HEADERS = [
  "code",
  "redeemed_at",
  "amount",
  "customer_name",
  "booking_reference",
  "service_date",
  "note"
];

const LEGACY_GIFT_CARD_CHANGES_TEMPLATE_HEADERS = [
  "code",
  "changed_at",
  "change_type",
  "amount_delta",
  "note",
  "performed_by"
];

function downloadCsvTemplate(filename: string, headers: string[]) {
  const blob = new Blob([`${headers.join(",")}\n`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function createStorefrontDraft(overrides: Partial<GiftCardStorefrontSettings> = {}): GiftCardStorefrontDraft {
  const base = {
    enabled: true,
    heroEyebrow: "Gift Cards",
    heroHeadline: "Send a stored-value gift card in minutes.",
    heroSubheadline: "Choose an amount, add a note, and we'll deliver the code by email right after payment clears.",
    heroImageUrl: "",
    presetAmounts: [5000, 10000, 15000, 20000, 25000, 30000, 50000],
    minAmount: 1000,
    maxAmount: 1000000,
    defaultThemeId: "",
    legalDisclaimer: "",
    printFooterText: "",
    schedulingPolicy: "issue_now_send_later",
    buyerRevealPolicy: "follow_schedule",
    allowPrintPage: true,
    allowPdfDownload: true,
    primaryCtaLabel: "Continue to payment",
    secondaryCtaLabel: "Book a service"
  } satisfies GiftCardStorefrontSettings;

  const merged = {
    ...base,
    ...overrides
  };

  return {
    ...merged,
    presetAmountsText: (merged.presetAmounts ?? []).map((amount) => String(Math.round(Number(amount ?? 0) / 100))).join(", ")
  };
}

function parsePresetAmountsText(value: string) {
  return Array.from(
    new Set(
      String(value ?? "")
        .split(",")
        .map((part) => Math.round(Number(part.trim() || 0) * 100))
        .filter((amount) => amount >= 1000)
    )
  ).sort((left, right) => left - right);
}

const GIFT_CARD_OCCASION_OPTIONS = [
  "general",
  "birthday",
  "get_well",
  "anniversary",
  "congratulations",
  "thank_you",
  "housewarming",
  "new_baby",
  "graduation",
  "wedding",
  "holiday",
  "sympathy",
  "just_because"
] as const;

function normalizeHexColorForPicker(value: string, fallback: string) {
  return /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(String(value ?? "").trim()) ? String(value).trim() : fallback;
}

function createThemeDraft(overrides: Partial<GiftCardTheme> = {}) {
  return {
    name: String(overrides.name ?? ""),
    slug: String(overrides.slug ?? ""),
    occasionType: String(overrides.occasionType ?? "general"),
    backgroundImageUrl: String(overrides.backgroundImageUrl ?? ""),
    accentColor: String(overrides.accentColor ?? "#c65f3f"),
    textColor: String(overrides.textColor ?? "#18261f"),
    headline: String(overrides.headline ?? ""),
    subheadline: String(overrides.subheadline ?? ""),
    displayOrder: Number(overrides.displayOrder ?? 0),
    active: Boolean(overrides.active ?? true),
    isDefault: Boolean(overrides.isDefault ?? false),
    config: overrides.config ?? {}
  };
}

export function GiftCardsPage() {
  const queryClient = useQueryClient();
  const paymentCardRef = useRef<StripePaymentCardHandle | null>(null);
  const legacyCardsInputRef = useRef<HTMLInputElement | null>(null);
  const legacyRedemptionsInputRef = useRef<HTMLInputElement | null>(null);
  const legacyChangesInputRef = useRef<HTMLInputElement | null>(null);
  const changesSectionRef = useRef<HTMLDivElement | null>(null);
  const redemptionsSectionRef = useRef<HTMLDivElement | null>(null);
  const [activeTab, setActiveTab] = useState<"cards" | "orders" | "themes" | "storefront">("cards");
  const [search, setSearch] = useState("");
  const [selectedGiftCardId, setSelectedGiftCardId] = useState("");
  const [selectedThemeId, setSelectedThemeId] = useState("");
  const [isCreatingTheme, setIsCreatingTheme] = useState(false);
  const [issueMode, setIssueMode] = useState<"card" | "free">("card");
  const [adminPurchaseSession, setAdminPurchaseSession] = useState<AdminGiftCardPurchaseSession | null>(null);
  const [pollingOrderId, setPollingOrderId] = useState("");
  const [issueSuccess, setIssueSuccess] = useState<AdminGiftCardIssueSuccess | null>(null);
  const [legacyCardsCsv, setLegacyCardsCsv] = useState("");
  const [legacyCardsFileName, setLegacyCardsFileName] = useState("");
  const [legacyRedemptionsCsv, setLegacyRedemptionsCsv] = useState("");
  const [legacyRedemptionsFileName, setLegacyRedemptionsFileName] = useState("");
  const [legacyChangesCsv, setLegacyChangesCsv] = useState("");
  const [legacyChangesFileName, setLegacyChangesFileName] = useState("");
  const [legacyImportFeedback, setLegacyImportFeedback] = useState<string | null>(null);
  const [legacyImportPreview, setLegacyImportPreview] = useState<GiftCardLegacyImportPreview | null>(null);
  const [sendEmailsOnImport, setSendEmailsOnImport] = useState(false);

  const cardsQuery = useQuery({
    queryKey: ["gift-cards-admin", search],
    queryFn: () => apiFetch<{ items: GiftCardListItem[] }>(`/api/gift-cards?q=${encodeURIComponent(search)}`)
  });
  const ordersQuery = useQuery({
    queryKey: ["gift-card-orders-admin", search],
    queryFn: () => apiFetch<{ items: GiftCardOrderItem[] }>(`/api/gift-cards/orders?q=${encodeURIComponent(search)}`)
  });
  const themesQuery = useQuery({
    queryKey: ["gift-card-themes-admin"],
    queryFn: () => apiFetch<{ items: GiftCardTheme[] }>("/api/gift-cards/themes")
  });
  const storefrontQuery = useQuery({
    queryKey: ["gift-card-storefront-admin"],
    queryFn: () => apiFetch<GiftCardStorefrontSettings>("/api/gift-cards/storefront")
  });
  const detailQuery = useQuery({
    queryKey: ["gift-card-detail", selectedGiftCardId],
    queryFn: () => apiFetch<GiftCardDetail>(`/api/gift-cards/${selectedGiftCardId}`),
    enabled: Boolean(selectedGiftCardId)
  });
  const redemptionsQuery = useQuery({
    queryKey: ["gift-card-redemptions", selectedGiftCardId],
    queryFn: () => apiFetch<{ items: GiftCardRedemptionItem[] }>(`/api/gift-cards/${selectedGiftCardId}/redemptions`),
    enabled: Boolean(selectedGiftCardId)
  });
  const orderDetailQuery = useQuery({
    queryKey: ["gift-card-order-detail-admin", pollingOrderId],
    queryFn: () => apiFetch<GiftCardOrderDetail>(`/api/gift-cards/orders/${pollingOrderId}`),
    enabled: Boolean(pollingOrderId),
    refetchInterval: (query) => {
      const status = String((query.state.data as GiftCardOrderDetail | undefined)?.status ?? "").trim().toLowerCase();
      return status === "fulfilled" ? false : 1500;
    }
  });

  const cardItems = cardsQuery.data?.items ?? [];
  const orderItems = ordersQuery.data?.items ?? [];
  const themeItems = (themesQuery.data?.items ?? []).filter((theme) =>
    !search
      ? true
      : [theme.name, theme.slug, theme.occasionType, theme.headline, theme.subheadline].join(" ").toLowerCase().includes(search.toLowerCase())
  );
  const selectedTheme =
    (themesQuery.data?.items ?? []).find((theme) => theme.id === selectedThemeId) ??
    (themesQuery.data?.items ?? [])[0] ??
    null;

  const issueForm = useForm<any>({
    resolver: zodResolver(giftCardAdminPurchaseSessionInputSchema),
    defaultValues: {
      amount: 10000,
      purchaserName: "",
      purchaserEmail: "",
      recipientName: "",
      recipientEmail: "",
      message: "",
      internalNote: "",
      giftCardThemeId: ""
    }
  });
  useEffect(() => {
    if (!cardItems.length) {
      if (selectedGiftCardId) {
        setSelectedGiftCardId("");
      }
      return;
    }

    if (!selectedGiftCardId || !cardItems.some((item) => item.id === selectedGiftCardId)) {
      setSelectedGiftCardId(cardItems[0].id);
    }
  }, [cardItems, selectedGiftCardId]);

  useEffect(() => {
    if (!(themesQuery.data?.items ?? []).length) {
      if (selectedThemeId) {
        setSelectedThemeId("");
      }
      return;
    }

    if (!selectedThemeId || !(themesQuery.data?.items ?? []).some((item) => item.id === selectedThemeId)) {
      setSelectedThemeId(themesQuery.data?.items?.[0].id ?? "");
      setIsCreatingTheme(false);
    }
  }, [selectedThemeId, themesQuery.data?.items]);

  const adjustmentForm = useForm<any>({
    resolver: zodResolver(giftCardAdjustmentInputSchema),
    defaultValues: {
      amountDelta: 0,
      note: ""
    }
  });
  const themeForm = useForm<any>({
    resolver: zodResolver(giftCardThemeInputSchema),
    defaultValues: createThemeDraft()
  });
  const storefrontForm = useForm<any>({
    resolver: zodResolver(giftCardStorefrontSettingsSchema),
    defaultValues: createStorefrontDraft()
  });

  useEffect(() => {
    const defaultThemeId = (themesQuery.data?.items ?? []).find((theme) => theme.isDefault)?.id ?? themesQuery.data?.items?.[0]?.id ?? "";
    if (defaultThemeId && !issueForm.getValues("giftCardThemeId")) {
      issueForm.setValue("giftCardThemeId", defaultThemeId, {
        shouldDirty: false,
        shouldValidate: true
      });
    }
  }, [issueForm, themesQuery.data?.items]);

  useEffect(() => {
    if (!storefrontQuery.data) {
      return;
    }

    storefrontForm.reset(createStorefrontDraft(storefrontQuery.data));
  }, [storefrontForm, storefrontQuery.data]);

  useEffect(() => {
    if (!selectedTheme || isCreatingTheme) {
      return;
    }

    themeForm.reset(createThemeDraft(selectedTheme));
  }, [isCreatingTheme, selectedTheme, themeForm]);

  const refreshGiftCards = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["gift-cards-admin"] }),
      queryClient.invalidateQueries({ queryKey: ["gift-card-orders-admin"] }),
      queryClient.invalidateQueries({ queryKey: ["gift-card-detail"] }),
      queryClient.invalidateQueries({ queryKey: ["gift-card-order-detail-admin"] }),
      queryClient.invalidateQueries({ queryKey: ["gift-card-redemptions"] })
    ]);
  };

  const refreshThemes = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["gift-card-themes-admin"] }),
      queryClient.invalidateQueries({ queryKey: ["gift-card-storefront-admin"] }),
      queryClient.invalidateQueries({ queryKey: ["gift-cards-admin"] }),
      queryClient.invalidateQueries({ queryKey: ["gift-card-orders-admin"] }),
      queryClient.invalidateQueries({ queryKey: ["gift-card-detail"] }),
      queryClient.invalidateQueries({ queryKey: ["gift-card-order-detail-admin"] }),
      queryClient.invalidateQueries({ queryKey: ["gift-card-redemptions"] })
    ]);
  };

  const refreshStorefront = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["gift-card-storefront-admin"] }),
      queryClient.invalidateQueries({ queryKey: ["gift-card-themes-admin"] })
    ]);
  };

  const freeIssueMutation = useMutation({
    mutationFn: (values: any) =>
      apiFetch<{
        orderId: string;
        giftCardId: string;
        giftCardCode: string;
        giftCardTheme: GiftCardThemeSnapshot;
      }>("/api/gift-cards/admin/issue-free", {
        method: "POST",
        body: JSON.stringify(values)
      }),
    onSuccess: async (result) => {
      const retainedThemeId = issueForm.getValues("giftCardThemeId");
      issueForm.reset({
        amount: 10000,
        purchaserName: "",
        purchaserEmail: "",
        recipientName: "",
        recipientEmail: "",
        message: "",
        internalNote: "",
        giftCardThemeId: retainedThemeId
      });
      setAdminPurchaseSession(null);
      setPollingOrderId("");
      setIssueSuccess({
        paymentMode: "free",
        orderId: result.orderId,
        giftCardId: result.giftCardId,
        giftCardCode: result.giftCardCode,
        giftCardTheme: result.giftCardTheme
      });
      await refreshGiftCards();
      setActiveTab("cards");
      setSelectedGiftCardId(result.giftCardId);
    }
  });

  const adminPurchaseSessionMutation = useMutation({
    mutationFn: (values: any) =>
      apiFetch<AdminGiftCardPurchaseSession>("/api/gift-cards/admin/purchase-session", {
        method: "POST",
        body: JSON.stringify(values)
      }),
    onSuccess: async (session) => {
      setIssueSuccess(null);
      setPollingOrderId("");
      setAdminPurchaseSession(session);
      setActiveTab("orders");
      await refreshGiftCards();
    }
  });

  const adjustmentMutation = useMutation({
    mutationFn: (values: any) =>
      apiFetch(`/api/gift-cards/${selectedGiftCardId}/adjust`, {
        method: "POST",
        body: JSON.stringify(values)
      }),
    onSuccess: async () => {
      adjustmentForm.reset({ amountDelta: 0, note: "" });
      await refreshGiftCards();
    }
  });

  const statusMutation = useMutation({
    mutationFn: (status: "active" | "inactive") =>
      apiFetch(`/api/gift-cards/${selectedGiftCardId}/status`, {
        method: "POST",
        body: JSON.stringify({ status })
      }),
    onSuccess: refreshGiftCards
  });

  const resendMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/api/gift-cards/${selectedGiftCardId}/resend`, {
        method: "POST"
      }),
    onSuccess: refreshGiftCards
  });

  const receiptMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/api/gift-cards/${selectedGiftCardId}/send-receipt`, {
        method: "POST"
      }),
    onSuccess: refreshGiftCards
  });

  const legacyImportPreviewMutation = useMutation({
    mutationFn: () =>
      apiFetch<GiftCardLegacyImportPreview>("/api/gift-cards/import/preview", {
        method: "POST",
        body: JSON.stringify({
          cardsCsv: legacyCardsCsv,
          redemptionsCsv: legacyRedemptionsCsv,
          changesCsv: legacyChangesCsv,
          sendEmails: sendEmailsOnImport
        })
      }),
    onSuccess: (result) => {
      setLegacyImportPreview(result);
      setLegacyImportFeedback(
        `Preview ready: ${result.summary.cards} card row${result.summary.cards === 1 ? "" : "s"}, ${result.summary.createCount} create, ${result.summary.updateCount} update, ${result.summary.errorCount} error${result.summary.errorCount === 1 ? "" : "s"}.`
      );
    }
  });

  const legacyImportMutation = useMutation({
    mutationFn: () =>
      apiFetch<GiftCardLegacyImportResult>("/api/gift-cards/import", {
        method: "POST",
        body: JSON.stringify({
          cardsCsv: legacyCardsCsv,
          redemptionsCsv: legacyRedemptionsCsv,
          changesCsv: legacyChangesCsv,
          sendEmails: sendEmailsOnImport
        })
      }),
    onSuccess: async (result) => {
      setLegacyImportFeedback(
        `Imported ${result.imported} gift card${result.imported === 1 ? "" : "s"}: ${result.created} new, ${result.updated} updated, ${result.importedTransactions} legacy ledger rows, ${result.emailed} email${result.emailed === 1 ? "" : "s"} sent.`
      );
      await refreshGiftCards();
      setActiveTab("cards");
      if (result.ids[0]) {
        setSelectedGiftCardId(result.ids[0]);
      }
    }
  });

  const themeCreateMutation = useMutation({
    mutationFn: (values: any) =>
      apiFetch<GiftCardTheme>("/api/gift-cards/themes", {
        method: "POST",
        body: JSON.stringify(values)
      }),
    onSuccess: async (result) => {
      await refreshThemes();
      setIsCreatingTheme(false);
      setActiveTab("themes");
      setSelectedThemeId(result.id);
    }
  });

  const themeUpdateMutation = useMutation({
    mutationFn: (values: any) =>
      apiFetch<GiftCardTheme>(`/api/gift-cards/themes/${selectedThemeId}`, {
        method: "PUT",
        body: JSON.stringify(values)
      }),
    onSuccess: async () => {
      await refreshThemes();
      setIsCreatingTheme(false);
    }
  });

  useEffect(() => {
    const order = orderDetailQuery.data;
    if (!pollingOrderId || !order || String(order.status ?? "").trim().toLowerCase() !== "fulfilled") {
      return;
    }

    setIssueSuccess({
      paymentMode: "card",
      orderId: order.id,
      giftCardId: order.giftCardId ?? null,
      giftCardCode: order.giftCardCode,
      giftCardTheme: order.giftCardTheme ?? null
    });
    setAdminPurchaseSession(null);
    setPollingOrderId("");
    void refreshGiftCards();
    setActiveTab("cards");
    if (order.giftCardId) {
      setSelectedGiftCardId(order.giftCardId);
    }
  }, [orderDetailQuery.data, pollingOrderId]);

  const themeStatusMutation = useMutation({
    mutationFn: (values: { active: boolean; isDefault?: boolean }) =>
      apiFetch<GiftCardTheme>(`/api/gift-cards/themes/${selectedThemeId}/status`, {
        method: "POST",
        body: JSON.stringify(values)
      }),
    onSuccess: async () => {
      await refreshThemes();
      setIsCreatingTheme(false);
    }
  });

  const storefrontMutation = useMutation({
    mutationFn: (values: GiftCardStorefrontSettings) =>
      apiFetch<GiftCardStorefrontSettings>("/api/gift-cards/storefront", {
        method: "PUT",
        body: JSON.stringify(values)
      }),
    onSuccess: async (result) => {
      storefrontForm.reset(createStorefrontDraft(result));
      await refreshStorefront();
      setActiveTab("storefront");
    }
  });

  const detail = detailQuery.data ?? null;
  const currentStatusTone = detail?.status === "active" ? "success" : "warning";
  const issueAmountDollars = Math.round(Number(issueForm.watch("amount") ?? 0) / 100);
  const adjustmentAmountDollars = Number(adjustmentForm.watch("amountDelta") ?? 0) / 100;
  const selectedIssueThemeId = String(issueForm.watch("giftCardThemeId") ?? "").trim();
  const selectedIssueTheme =
    (themesQuery.data?.items ?? []).find((theme) => theme.id === selectedIssueThemeId) ??
    (themesQuery.data?.items ?? []).find((theme) => theme.isDefault) ??
    (themesQuery.data?.items ?? [])[0] ??
    null;
  const themeDraft = {
    name: String(themeForm.watch("name") ?? ""),
    slug: String(themeForm.watch("slug") ?? ""),
    occasionType: String(themeForm.watch("occasionType") ?? "general"),
    backgroundImageUrl: String(themeForm.watch("backgroundImageUrl") ?? ""),
    accentColor: String(themeForm.watch("accentColor") ?? "#c65f3f"),
    textColor: String(themeForm.watch("textColor") ?? "#18261f"),
    headline: String(themeForm.watch("headline") ?? ""),
    subheadline: String(themeForm.watch("subheadline") ?? "")
  };
  const accentColorPickerValue = normalizeHexColorForPicker(themeDraft.accentColor, "#c65f3f");
  const textColorPickerValue = normalizeHexColorForPicker(themeDraft.textColor, "#18261f");
  const storefrontDraft = createStorefrontDraft({
    enabled: Boolean(storefrontForm.watch("enabled") ?? true),
    heroEyebrow: String(storefrontForm.watch("heroEyebrow") ?? "Gift Cards"),
    heroHeadline: String(storefrontForm.watch("heroHeadline") ?? "Send a stored-value gift card in minutes."),
    heroSubheadline: String(
      storefrontForm.watch("heroSubheadline") ?? "Choose an amount, add a note, and we'll deliver the code by email right after payment clears."
    ),
    heroImageUrl: String(storefrontForm.watch("heroImageUrl") ?? ""),
    presetAmounts: parsePresetAmountsText(String(storefrontForm.watch("presetAmountsText") ?? "")),
    minAmount: Math.max(1000, Math.round(Number(storefrontForm.watch("minAmount") ?? 1000))),
    maxAmount: Math.max(1000, Math.round(Number(storefrontForm.watch("maxAmount") ?? 1000000))),
    defaultThemeId: String(storefrontForm.watch("defaultThemeId") ?? ""),
    legalDisclaimer: String(storefrontForm.watch("legalDisclaimer") ?? ""),
    printFooterText: String(storefrontForm.watch("printFooterText") ?? ""),
    schedulingPolicy: String(storefrontForm.watch("schedulingPolicy") ?? "issue_now_send_later") as GiftCardStorefrontSettings["schedulingPolicy"],
    buyerRevealPolicy: String(storefrontForm.watch("buyerRevealPolicy") ?? "follow_schedule") as GiftCardStorefrontSettings["buyerRevealPolicy"],
    allowPrintPage: Boolean(storefrontForm.watch("allowPrintPage") ?? true),
    allowPdfDownload: Boolean(storefrontForm.watch("allowPdfDownload") ?? true),
    primaryCtaLabel: String(storefrontForm.watch("primaryCtaLabel") ?? "Continue to payment"),
    secondaryCtaLabel: String(storefrontForm.watch("secondaryCtaLabel") ?? "Book a service")
  });
  const storefrontPurchaseUrl =
    typeof window === "undefined" ? "/gift-cards/purchase" : new URL("/gift-cards/purchase", window.location.origin).toString();

  const loadLegacyImportFile = async (
    file: File,
    setters: {
      setContents: (value: string) => void;
      setFileName: (value: string) => void;
    }
  ) => {
    const contents = await file.text();
    setters.setContents(contents);
    setters.setFileName(file.name);
    setLegacyImportFeedback(`${file.name} loaded. Review the preview before importing.`);
    setLegacyImportPreview(null);
  };

  const scrollToSection = (ref: RefObject<HTMLDivElement | null>) => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const beginCreateTheme = () => {
    const highestDisplayOrder = (themesQuery.data?.items ?? []).reduce(
      (max, item) => Math.max(max, Number(item.displayOrder ?? 0)),
      0
    );
    setIsCreatingTheme(true);
    setActiveTab("themes");
    themeForm.reset(
      createThemeDraft({
        displayOrder: highestDisplayOrder + 10,
        isDefault: !(themesQuery.data?.items ?? []).some((item) => item.isDefault)
      })
    );
  };

  return (
    <div className="stack">
      <SectionHeader title="Gift Cards" description="Issue, theme, track, resend, and adjust stored-value gift cards." />

      <div className="gift-cards-workflow">
        <div className="gift-card-admin-tools stack">
          <Card title="Issue a gift card" eyebrow="Admin tool">
            <form
              className="stack"
              onSubmit={issueForm.handleSubmit(async (values) => {
                setIssueSuccess(null);
                setPollingOrderId("");
                if (issueMode === "free") {
                  await freeIssueMutation.mutateAsync(values);
                  return;
                }
                await adminPurchaseSessionMutation.mutateAsync(values);
              })}
            >
              <div className="hero__actions">
                <label className="booking-inline-label">
                  <input
                    checked={issueMode === "card"}
                    name="gift-card-issue-mode"
                    type="radio"
                    onChange={() => {
                      setIssueMode("card");
                      setIssueSuccess(null);
                    }}
                  />
                  Credit Card
                </label>
                <label className="booking-inline-label">
                  <input
                    checked={issueMode === "free"}
                    name="gift-card-issue-mode"
                    type="radio"
                    onChange={() => {
                      setIssueMode("free");
                      setAdminPurchaseSession(null);
                      setPollingOrderId("");
                      setIssueSuccess(null);
                    }}
                  />
                  Free
                </label>
              </div>
              <Field label="Amount">
                <input
                  type="number"
                  min="10"
                  step="1"
                  disabled={Boolean(adminPurchaseSession)}
                  value={issueAmountDollars}
                  onChange={(event) =>
                    issueForm.setValue("amount", Math.max(0, Math.round(Number(event.target.value || 0) * 100)), {
                      shouldDirty: true,
                      shouldValidate: true
                    })
                  }
                />
              </Field>
              <Field label="Theme">
                <select disabled={Boolean(adminPurchaseSession)} {...issueForm.register("giftCardThemeId")}>
                  <option value="">Use default theme</option>
                  {(themesQuery.data?.items ?? [])
                    .filter((theme) => theme.active)
                    .map((theme) => (
                      <option key={theme.id} value={theme.id}>
                        {theme.name}
                        {theme.isDefault ? " (Default)" : ""}
                      </option>
                    ))}
                </select>
              </Field>
              <div className="grid two">
                <Field label="Purchaser name">
                  <input disabled={Boolean(adminPurchaseSession)} {...issueForm.register("purchaserName")} />
                </Field>
                <Field label="Purchaser email">
                  <input disabled={Boolean(adminPurchaseSession)} type="email" {...issueForm.register("purchaserEmail")} />
                </Field>
                <Field label="Recipient name">
                  <input disabled={Boolean(adminPurchaseSession)} {...issueForm.register("recipientName")} />
                </Field>
                <Field label="Recipient email">
                  <input disabled={Boolean(adminPurchaseSession)} type="email" {...issueForm.register("recipientEmail")} />
                </Field>
              </div>
              <Field label="Gift message">
                <textarea disabled={Boolean(adminPurchaseSession)} {...issueForm.register("message")} rows={3} />
              </Field>
              <Field label="Internal note">
                <input disabled={Boolean(adminPurchaseSession)} {...issueForm.register("internalNote")} placeholder="Comped for service recovery" />
              </Field>
              <GiftCardThemePreview
                theme={selectedIssueTheme}
                amount={Number(issueForm.watch("amount") ?? 0)}
                recipientName={String(issueForm.watch("recipientName") ?? "Recipient name")}
                purchaserName={String(issueForm.watch("purchaserName") ?? "Purchaser name")}
                message={String(issueForm.watch("message") ?? "Use this gift card whenever a little more breathing room would help.")}
                code={issueSuccess?.giftCardCode || undefined}
                caption="This snapshot will be stored on the order and issued card."
              />

              {issueSuccess ? (
                <div className="quote-callout">
                  <p className="eyebrow">{issueSuccess.paymentMode === "free" ? "Complimentary issue complete" : "Payment complete"}</p>
                  <p>
                    Code <strong>{issueSuccess.giftCardCode}</strong>
                  </p>
                  <p>Order {issueSuccess.orderId}</p>
                  <p>{issueSuccess.giftCardId ? `Gift card ${issueSuccess.giftCardId}` : "Gift card is ready in Orders."}</p>
                </div>
              ) : null}

              {adminPurchaseSession ? (
                <div className="quote-callout">
                  <p className="eyebrow">Payment session ready</p>
                  <p>Order {adminPurchaseSession.orderId}</p>
                  <p>Theme {adminPurchaseSession.giftCardTheme?.name || "Default"}</p>
                </div>
              ) : null}

              {pollingOrderId ? (
                <div className="quote-callout">
                  <p className="eyebrow">Waiting for fulfillment</p>
                  <p>The payment cleared with Stripe. We&apos;re waiting for the issued code to appear.</p>
                  <p>Order {pollingOrderId}</p>
                </div>
              ) : null}

              {freeIssueMutation.error ? <p className="error-text">{(freeIssueMutation.error as Error).message}</p> : null}
              {adminPurchaseSessionMutation.error ? (
                <p className="error-text">{(adminPurchaseSessionMutation.error as Error).message}</p>
              ) : null}
              {orderDetailQuery.error && pollingOrderId ? <p className="error-text">{(orderDetailQuery.error as Error).message}</p> : null}

              <div className="hero__actions">
                <Button
                  type="submit"
                  disabled={
                    freeIssueMutation.isPending ||
                    adminPurchaseSessionMutation.isPending ||
                    Boolean(adminPurchaseSession) ||
                    Boolean(pollingOrderId)
                  }
                >
                  {freeIssueMutation.isPending || adminPurchaseSessionMutation.isPending
                    ? issueMode === "free"
                      ? "Issuing..."
                      : "Preparing..."
                    : issueMode === "free"
                      ? "Issue gift card"
                      : "Continue to payment"}
                </Button>
                {(adminPurchaseSession || pollingOrderId || issueSuccess) ? (
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={() => {
                      setAdminPurchaseSession(null);
                      setPollingOrderId("");
                      setIssueSuccess(null);
                    }}
                  >
                    Start over
                  </Button>
                ) : null}
              </div>

              {adminPurchaseSession ? (
                <StripePaymentCard
                  ref={paymentCardRef}
                  session={adminPurchaseSession}
                  dueLabel={`Charge ${formatCurrency(adminPurchaseSession.amount)}`}
                  subtitle="Enter the payment card here to complete this admin gift card purchase."
                  onPaymentSuccess={() => {
                    setIssueSuccess(null);
                    setPollingOrderId(adminPurchaseSession.orderId);
                  }}
                />
              ) : null}
            </form>
          </Card>

          <Card title="Import legacy gift cards" eyebrow="Migration tool">
            <div className="stack">
              <input
                ref={legacyCardsInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: "none" }}
                onChange={async (event) => {
                  const file = event.target.files?.[0];
                  if (!file) {
                    return;
                  }
                  try {
                    await loadLegacyImportFile(file, {
                      setContents: setLegacyCardsCsv,
                      setFileName: setLegacyCardsFileName
                    });
                  } catch (error) {
                    setLegacyImportFeedback(error instanceof Error ? error.message : "Gift card file load failed.");
                  }
                  event.currentTarget.value = "";
                }}
              />
              <input
                ref={legacyRedemptionsInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: "none" }}
                onChange={async (event) => {
                  const file = event.target.files?.[0];
                  if (!file) {
                    return;
                  }
                  try {
                    await loadLegacyImportFile(file, {
                      setContents: setLegacyRedemptionsCsv,
                      setFileName: setLegacyRedemptionsFileName
                    });
                  } catch (error) {
                    setLegacyImportFeedback(error instanceof Error ? error.message : "Redemptions file load failed.");
                  }
                  event.currentTarget.value = "";
                }}
              />
              <input
                ref={legacyChangesInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: "none" }}
                onChange={async (event) => {
                  const file = event.target.files?.[0];
                  if (!file) {
                    return;
                  }
                  try {
                    await loadLegacyImportFile(file, {
                      setContents: setLegacyChangesCsv,
                      setFileName: setLegacyChangesFileName
                    });
                  } catch (error) {
                    setLegacyImportFeedback(error instanceof Error ? error.message : "Changes file load failed.");
                  }
                  event.currentTarget.value = "";
                }}
              />

              <p className="card-copy">
                Import your current platform&apos;s gift card codes and balances before cutover. The cards CSV is required. Redemptions and changes are optional when you have legacy history exports.
              </p>

              <div className="hero__actions">
                <Button variant="secondary" type="button" onClick={() => downloadCsvTemplate("gift-cards-template.csv", LEGACY_GIFT_CARD_TEMPLATE_HEADERS)}>
                  Download cards template
                </Button>
                <Button variant="secondary" type="button" onClick={() => downloadCsvTemplate("gift-card-redemptions-template.csv", LEGACY_GIFT_CARD_REDEMPTIONS_TEMPLATE_HEADERS)}>
                  Redemptions template
                </Button>
                <Button variant="secondary" type="button" onClick={() => downloadCsvTemplate("gift-card-changes-template.csv", LEGACY_GIFT_CARD_CHANGES_TEMPLATE_HEADERS)}>
                  Changes template
                </Button>
              </div>

              <div className="grid two">
                <div className="stack stack--tight">
                  <p className="eyebrow">Cards CSV</p>
                  <div className="hero__actions">
                    <Button variant="secondary" type="button" onClick={() => legacyCardsInputRef.current?.click()}>
                      {legacyCardsFileName ? "Replace cards file" : "Upload cards file"}
                    </Button>
                    {legacyCardsFileName ? (
                      <Button
                        variant="secondary"
                        type="button"
                        onClick={() => {
                          setLegacyCardsCsv("");
                          setLegacyCardsFileName("");
                          setLegacyImportPreview(null);
                        }}
                      >
                        Clear
                      </Button>
                    ) : null}
                  </div>
                  <p className="card-copy">{legacyCardsFileName || "No cards CSV loaded yet."}</p>
                </div>

                <div className="stack stack--tight">
                  <p className="eyebrow">Redemptions CSV</p>
                  <div className="hero__actions">
                    <Button variant="secondary" type="button" onClick={() => legacyRedemptionsInputRef.current?.click()}>
                      {legacyRedemptionsFileName ? "Replace redemptions file" : "Upload redemptions file"}
                    </Button>
                    {legacyRedemptionsFileName ? (
                      <Button
                        variant="secondary"
                        type="button"
                        onClick={() => {
                          setLegacyRedemptionsCsv("");
                          setLegacyRedemptionsFileName("");
                          setLegacyImportPreview(null);
                        }}
                      >
                        Clear
                      </Button>
                    ) : null}
                  </div>
                  <p className="card-copy">{legacyRedemptionsFileName || "Optional. Use this when you can export legacy redemption history."}</p>
                </div>

                <div className="stack stack--tight">
                  <p className="eyebrow">Changes CSV</p>
                  <div className="hero__actions">
                    <Button variant="secondary" type="button" onClick={() => legacyChangesInputRef.current?.click()}>
                      {legacyChangesFileName ? "Replace changes file" : "Upload changes file"}
                    </Button>
                    {legacyChangesFileName ? (
                      <Button
                        variant="secondary"
                        type="button"
                        onClick={() => {
                          setLegacyChangesCsv("");
                          setLegacyChangesFileName("");
                          setLegacyImportPreview(null);
                        }}
                      >
                        Clear
                      </Button>
                    ) : null}
                  </div>
                  <p className="card-copy">{legacyChangesFileName || "Optional. Use this for legacy balance adjustments or manual changes."}</p>
                </div>

                <div className="stack stack--tight">
                  <p className="eyebrow">Delivery during import</p>
                  <label className="booking-inline-label">
                    <input type="checkbox" checked={sendEmailsOnImport} onChange={(event) => setSendEmailsOnImport(event.target.checked)} />
                    Send recipient emails during import
                  </label>
                  <p className="card-copy">Leave this off for your cutover rehearsal. Imported cards do not send emails unless you explicitly enable it.</p>
                </div>
              </div>

              {legacyImportFeedback ? <p className="card-copy">{legacyImportFeedback}</p> : null}
              {legacyImportPreviewMutation.error ? <p className="error-text">{(legacyImportPreviewMutation.error as Error).message}</p> : null}
              {legacyImportMutation.error ? <p className="error-text">{(legacyImportMutation.error as Error).message}</p> : null}

              <div className="hero__actions">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={!legacyCardsCsv || legacyImportPreviewMutation.isPending || legacyImportMutation.isPending}
                  onClick={() => legacyImportPreviewMutation.mutate()}
                >
                  {legacyImportPreviewMutation.isPending ? "Previewing..." : "Preview import"}
                </Button>
                <Button
                  type="button"
                  disabled={
                    !legacyCardsCsv ||
                    legacyImportMutation.isPending ||
                    legacyImportPreviewMutation.isPending ||
                    Boolean(legacyImportPreview?.errors.length)
                  }
                  onClick={() => legacyImportMutation.mutate()}
                >
                  {legacyImportMutation.isPending ? "Importing..." : "Import gift cards"}
                </Button>
              </div>

              {legacyImportPreview ? (
                <div className="stack">
                  <div className="customer-card-grid">
                    <div className="booking-meta-card">
                      <p className="eyebrow">Cards</p>
                      <strong>{legacyImportPreview.summary.cards}</strong>
                      <p>{legacyImportPreview.summary.createCount} create / {legacyImportPreview.summary.updateCount} update</p>
                    </div>
                    <div className="booking-meta-card">
                      <p className="eyebrow">History</p>
                      <strong>{legacyImportPreview.summary.redemptions}</strong>
                      <p>{legacyImportPreview.summary.changes} change row{legacyImportPreview.summary.changes === 1 ? "" : "s"}</p>
                    </div>
                    <div className="booking-meta-card">
                      <p className="eyebrow">Errors</p>
                      <strong>{legacyImportPreview.summary.errorCount}</strong>
                      <p>{legacyImportPreview.summary.errorCount ? "Fix these before importing." : "Preview is ready to import."}</p>
                    </div>
                  </div>

                  {legacyImportPreview.errors.length ? (
                    <div className="quote-callout">
                      <p className="eyebrow">Import issues</p>
                      {legacyImportPreview.errors.map((error) => (
                        <p key={error}>{error}</p>
                      ))}
                    </div>
                  ) : null}

                  {legacyImportPreview.items.length ? (
                    <div className="table-wrap">
                      <table className="table customer-table booking-table">
                        <thead>
                          <tr>
                            <th>Code</th>
                            <th>Sender</th>
                            <th>Recipient</th>
                            <th>Balances</th>
                            <th>History</th>
                          </tr>
                        </thead>
                        <tbody>
                          {legacyImportPreview.items.slice(0, 12).map((item) => (
                            <tr key={item.code}>
                              <td>
                                <strong>{item.code}</strong>
                                <span>{item.action === "create" ? "Will create" : "Will update"}</span>
                              </td>
                              <td>
                                <strong>{item.purchaserName || "Unknown sender"}</strong>
                                <span>{formatPaymentModeLabel(item.paymentMode)}</span>
                              </td>
                              <td>
                                <strong>{item.recipientName || "Recipient"}</strong>
                                <span>{formatDateTime(item.issuedAt)}</span>
                              </td>
                              <td>
                                <strong>{formatCurrency(item.currentBalance)}</strong>
                                <span>of {formatCurrency(item.originalAmount)}</span>
                              </td>
                              <td>
                                <strong>{item.redemptionCount} redemption{item.redemptionCount === 1 ? "" : "s"}</strong>
                                <span>
                                  {item.changeCount} change row{item.changeCount === 1 ? "" : "s"}
                                  {item.syntheticRedeemedAmount > 0 ? ` | synthetic spent ${formatCurrency(item.syntheticRedeemedAmount)}` : ""}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </Card>
        </div>

        <Card title="Browse gift cards" eyebrow="Cards, orders, and themes" className="gift-card-browser-card">
          <div className="stack">
            <div className="hero__actions">
              <Button variant={activeTab === "cards" ? "primary" : "secondary"} onClick={() => setActiveTab("cards")}>
                Cards
              </Button>
              <Button variant={activeTab === "orders" ? "primary" : "secondary"} onClick={() => setActiveTab("orders")}>
                Orders
              </Button>
              <Button variant={activeTab === "themes" ? "primary" : "secondary"} onClick={() => setActiveTab("themes")}>
                Themes
              </Button>
              <Button variant={activeTab === "storefront" ? "primary" : "secondary"} onClick={() => setActiveTab("storefront")}>
                Storefront
              </Button>
            </div>
            <Field label="Search">
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={
                  activeTab === "themes"
                    ? "Search theme name, slug, or occasion"
                    : activeTab === "storefront"
                      ? "Storefront settings are edited below"
                    : "Search code, purchaser, recipient, or theme"
                }
                disabled={activeTab === "storefront"}
              />
            </Field>

            {activeTab === "cards" ? (
              <>
                {cardsQuery.isLoading ? <p className="card-copy">Loading gift cards...</p> : null}
                {cardsQuery.error ? <p className="error-text">{(cardsQuery.error as Error).message}</p> : null}
                {!cardsQuery.isLoading && !cardsQuery.error ? (
                  cardItems.length ? (
                    <div className="table-wrap">
                      <table className="table customer-table booking-table">
                        <thead>
                          <tr>
                            <th>Sender</th>
                            <th>Recipient</th>
                            <th>Purchase details</th>
                            <th>Code</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cardItems.map((item) => (
                            <tr key={item.id}>
                              <td>
                                <strong>{item.purchaserName || "Unknown sender"}</strong>
                                <span>{item.purchaserEmail || formatOrderSourceLabel(item.source)}</span>
                              </td>
                              <td>
                                <strong>{item.recipientName || "Recipient"}</strong>
                                <span>{item.recipientEmail || "No email"}</span>
                              </td>
                              <td>
                                <strong>Balance: {formatCurrency(Number(item.balanceAmount ?? 0))}</strong>
                                <span>Amount: {formatCurrency(Number(item.originalAmount ?? 0))}</span>
                                <span>Created: {formatDateTime(item.createdAt)}</span>
                                <span>
                                  {formatOrderSourceLabel(item.source)} / {formatPaymentModeLabel(item.paymentMode)} / {item.status}
                                </span>
                              </td>
                              <td>
                                <strong>{item.code}</strong>
                                <span>{item.giftCardTheme?.name || "Theme pending"}</span>
                              </td>
                              <td>
                                <div className="table-action-row">
                                  <Button variant="ghost" onClick={() => setSelectedGiftCardId(item.id)}>
                                    Open
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="card-copy">No gift cards match this search yet.</p>
                  )
                ) : null}
              </>
            ) : null}

            {activeTab === "orders" ? (
              <>
                {ordersQuery.isLoading ? <p className="card-copy">Loading gift card orders...</p> : null}
                {ordersQuery.error ? <p className="error-text">{(ordersQuery.error as Error).message}</p> : null}
                {!ordersQuery.isLoading && !ordersQuery.error ? (
                  orderItems.length ? (
                    <div className="table-wrap">
                      <table className="table customer-table booking-table">
                        <thead>
                          <tr>
                            <th>Created</th>
                            <th>Source</th>
                            <th>Recipient</th>
                            <th>Status</th>
                            <th>Amount</th>
                            <th>Code</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orderItems.map((item) => (
                            <tr key={item.id}>
                              <td>{formatDateTime(item.createdAt)}</td>
                              <td>
                                <strong>{formatOrderSourceLabel(item.source)}</strong>
                                <span>{formatPaymentModeLabel(item.paymentMode)}</span>
                              </td>
                              <td>
                                <strong>{item.recipientName || "Recipient"}</strong>
                                <span>{item.recipientEmail || "No email"}</span>
                              </td>
                              <td>
                                <Badge tone={item.status === "fulfilled" ? "success" : "info"}>{item.status}</Badge>
                                <div>{item.giftCardTheme?.name || "Theme pending"}</div>
                                <div>{item.publicStatus ? item.publicStatus.replace(/_/g, " ") : item.fulfilledAt ? `Fulfilled ${formatDateTime(item.fulfilledAt)}` : "Awaiting fulfillment"}</div>
                              </td>
                              <td>
                                <strong>{formatCurrency(Number(item.amount ?? 0))}</strong>
                                <span>
                                  {item.deliverySkipped
                                    ? "Manual share"
                                    : item.deliveredAt
                                      ? `Delivered ${formatDateTime(item.deliveredAt)}`
                                      : item.scheduledFor
                                        ? `Scheduled ${formatDateTime(item.scheduledFor)}`
                                        : "Not delivered"}
                                </span>
                              </td>
                              <td>
                                <strong>{item.giftCardCode || "Pending"}</strong>
                                <span>
                                  {[item.internalNote || "", item.schedulingPolicy || "", item.buyerRevealPolicy || ""].filter(Boolean).join(" | ") || "-"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="card-copy">No gift card orders match this search yet.</p>
                  )
                ) : null}
              </>
            ) : null}

            {activeTab === "themes" ? (
              <>
                <div className="hero__actions">
                  <Button variant="secondary" onClick={beginCreateTheme}>
                    New theme
                  </Button>
                </div>
                {themesQuery.isLoading ? <p className="card-copy">Loading themes...</p> : null}
                {themesQuery.error ? <p className="error-text">{(themesQuery.error as Error).message}</p> : null}
                {!themesQuery.isLoading && !themesQuery.error ? (
                  themeItems.length ? (
                    <div className="table-wrap">
                      <table className="table customer-table booking-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Occasion</th>
                            <th>Status</th>
                            <th>Default</th>
                            <th>Order</th>
                          </tr>
                        </thead>
                        <tbody>
                          {themeItems.map((theme) => (
                            <tr
                              key={theme.id}
                              onClick={() => {
                                setIsCreatingTheme(false);
                                setSelectedThemeId(theme.id);
                              }}
                            >
                              <td>
                                <strong>{theme.name}</strong>
                                <span>{theme.slug}</span>
                              </td>
                              <td>{theme.occasionType}</td>
                              <td>
                                <Badge tone={theme.active ? "success" : "warning"}>{theme.active ? "active" : "archived"}</Badge>
                              </td>
                              <td>{theme.isDefault ? "Default" : "-"}</td>
                              <td>{theme.displayOrder}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="card-copy">No themes match this search yet.</p>
                  )
                ) : null}
              </>
            ) : null}

            {activeTab === "storefront" ? (
              <form
                className="stack"
                onSubmit={storefrontForm.handleSubmit(async (values) => {
                  await storefrontMutation.mutateAsync({
                    ...values,
                    presetAmounts: parsePresetAmountsText(String(values.presetAmountsText ?? ""))
                  });
                })}
              >
                {storefrontQuery.isLoading ? <p className="card-copy">Loading storefront settings...</p> : null}
                {storefrontQuery.error ? <p className="error-text">{(storefrontQuery.error as Error).message}</p> : null}
                <div className="hero__actions">
                  <a className="button button--secondary" href={storefrontPurchaseUrl} target="_blank" rel="noreferrer">
                    Open storefront
                  </a>
                </div>
                <Field label="Enable storefront">
                  <label className="booking-inline-label">
                    <input type="checkbox" {...storefrontForm.register("enabled")} />
                    Accept public gift card purchases on this site
                  </label>
                </Field>
                <div className="grid two">
                  <Field label="Hero eyebrow">
                    <input {...storefrontForm.register("heroEyebrow")} />
                  </Field>
                  <Field label="Hero image URL">
                    <input type="url" {...storefrontForm.register("heroImageUrl")} placeholder="https://..." />
                  </Field>
                </div>
                <Field label="Hero headline">
                  <input {...storefrontForm.register("heroHeadline")} />
                </Field>
                <Field label="Hero subheadline">
                  <textarea {...storefrontForm.register("heroSubheadline")} rows={3} />
                </Field>
                <div className="grid two">
                  <Field label="Preset amounts (dollars)">
                    <input
                      value={String(storefrontForm.watch("presetAmountsText") ?? "")}
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        storefrontForm.setValue("presetAmountsText", nextValue, {
                          shouldDirty: true
                        });
                        storefrontForm.setValue("presetAmounts", parsePresetAmountsText(nextValue), {
                          shouldDirty: true,
                          shouldValidate: true
                        });
                      }}
                      placeholder="50, 100, 150, 200"
                    />
                  </Field>
                  <Field label="Default theme">
                    <select {...storefrontForm.register("defaultThemeId")}>
                      <option value="">Use the active default theme</option>
                      {(themesQuery.data?.items ?? [])
                        .filter((theme) => theme.active)
                        .map((theme) => (
                          <option key={theme.id} value={theme.id}>
                            {theme.name}
                          </option>
                        ))}
                    </select>
                  </Field>
                  <Field label="Minimum amount (cents)">
                    <input type="number" min="1000" step="100" {...storefrontForm.register("minAmount", { valueAsNumber: true })} />
                  </Field>
                  <Field label="Maximum amount (cents)">
                    <input type="number" min="1000" step="100" {...storefrontForm.register("maxAmount", { valueAsNumber: true })} />
                  </Field>
                  <Field label="Scheduling policy">
                    <select {...storefrontForm.register("schedulingPolicy")}>
                      <option value="issue_now_send_later">Issue now, send later</option>
                      <option value="issue_on_send">Issue at send time</option>
                    </select>
                  </Field>
                  <Field label="Buyer reveal policy">
                    <select {...storefrontForm.register("buyerRevealPolicy")}>
                      <option value="follow_schedule">Follow schedule</option>
                      <option value="always_reveal">Always reveal when a code exists</option>
                      <option value="hide_until_sent">Hide until sent</option>
                    </select>
                  </Field>
                </div>
                <div className="hero__actions">
                  <label className="booking-inline-label">
                    <input type="checkbox" {...storefrontForm.register("allowPrintPage")} />
                    Enable printable page
                  </label>
                  <label className="booking-inline-label">
                    <input type="checkbox" {...storefrontForm.register("allowPdfDownload")} />
                    Enable PDF download
                  </label>
                </div>
                <div className="grid two">
                  <Field label="Primary CTA label">
                    <input {...storefrontForm.register("primaryCtaLabel")} />
                  </Field>
                  <Field label="Secondary CTA label">
                    <input {...storefrontForm.register("secondaryCtaLabel")} />
                  </Field>
                </div>
                <Field label="Legal/disclaimer text">
                  <textarea {...storefrontForm.register("legalDisclaimer")} rows={4} />
                </Field>
                <Field label="Print/PDF footer text">
                  <textarea {...storefrontForm.register("printFooterText")} rows={4} />
                </Field>
                <div className="stack">
                  <p className="eyebrow">Storefront preview</p>
                  <GiftCardThemePreview
                    theme={
                      (themesQuery.data?.items ?? []).find((theme) => theme.id === storefrontDraft.defaultThemeId) ??
                      (themesQuery.data?.items ?? []).find((theme) => theme.isDefault) ??
                      (themesQuery.data?.items ?? [])[0] ??
                      null
                    }
                    amount={storefrontDraft.presetAmounts[0] ?? 10000}
                    recipientName="Recipient"
                    purchaserName="Purchaser"
                    message="This preview uses the current storefront defaults."
                    caption={storefrontDraft.printFooterText || storefrontDraft.legalDisclaimer || "Site-level storefront copy and policy preview."}
                  />
                </div>
                {storefrontMutation.error ? <p className="error-text">{(storefrontMutation.error as Error).message}</p> : null}
                <Button type="submit" disabled={storefrontMutation.isPending}>
                  {storefrontMutation.isPending ? "Saving storefront..." : "Save storefront settings"}
                </Button>
              </form>
            ) : null}
          </div>
        </Card>
      </div>

      {activeTab === "cards" ? (
        <Card title={detail?.code || "Gift card details"} eyebrow={detail ? detail.recipientName || "Gift card" : "Select a card"}>
          {!selectedGiftCardId ? <p className="card-copy">Select a gift card to view its balance history.</p> : null}
          {detailQuery.isLoading ? <p className="card-copy">Loading gift card details...</p> : null}
          {detailQuery.error ? <p className="error-text">{(detailQuery.error as Error).message}</p> : null}
          {detail ? (
            <div className="stack">
              <GiftCardThemePreview
                theme={detail.giftCardTheme}
                amount={detail.originalAmount}
                recipientName={detail.recipientName}
                purchaserName={detail.purchaserName}
                message={detail.giftMessage}
                code={detail.code}
                caption="Admin preview of the stored theme snapshot."
              />

              <div className="customer-card-grid">
                <div className="booking-meta-card">
                  <p className="eyebrow">Status</p>
                  <strong>{detail.code}</strong>
                  <p><Badge tone={currentStatusTone}>{detail.status}</Badge></p>
                </div>
                <div className="booking-meta-card">
                  <p className="eyebrow">Available balance</p>
                  <strong>{formatCurrency(Number(detail.balanceAmount ?? 0))}</strong>
                  <p>Original {formatCurrency(Number(detail.originalAmount ?? 0))}</p>
                </div>
                <div className="booking-meta-card">
                  <p className="eyebrow">Delivery</p>
                  <strong>{detail.recipientName || "Recipient"}</strong>
                  <p>{detail.recipientEmail || "No email on file"}</p>
                </div>
                <div className="booking-meta-card">
                  <p className="eyebrow">Issued</p>
                  <strong>{formatDateTime(detail.createdAt)}</strong>
                  <p>{formatOrderSourceLabel(detail.source)} / {formatPaymentModeLabel(detail.paymentMode)}</p>
                </div>
              </div>

              <div className="hero__actions">
                <Button variant="secondary" onClick={() => resendMutation.mutate()} disabled={resendMutation.isPending}>
                  {resendMutation.isPending ? "Sending..." : "Send gift card"}
                </Button>
                <Button variant="secondary" onClick={() => receiptMutation.mutate()} disabled={receiptMutation.isPending}>
                  {receiptMutation.isPending ? "Sending..." : "Send receipt"}
                </Button>
                <Button variant="secondary" type="button" onClick={() => scrollToSection(changesSectionRef)}>
                  View changes
                </Button>
                <Button variant="secondary" type="button" onClick={() => scrollToSection(redemptionsSectionRef)}>
                  View redemptions
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => statusMutation.mutate(detail.status === "active" ? "inactive" : "active")}
                  disabled={statusMutation.isPending}
                >
                  {statusMutation.isPending ? "Updating..." : detail.status === "active" ? "Deactivate" : "Activate"}
                </Button>
              </div>

              <div className="grid two">
                <div className="stack">
                  <p className="eyebrow">Purchaser</p>
                  <strong>{detail.purchaserName || "Unknown purchaser"}</strong>
                  <p>{detail.purchaserEmail || "No purchaser email"}</p>
                  {detail.giftMessage ? (
                    <div className="quote-callout">
                      <p className="eyebrow">Gift message</p>
                      <p>{detail.giftMessage}</p>
                    </div>
                  ) : null}
                </div>
                <div className="stack">
                  <p className="eyebrow">Order history</p>
                  <strong>{detail.orderId || "Manual issue"}</strong>
                  <p>
                    {detail.source ? `${formatOrderSourceLabel(detail.source)} / ${formatPaymentModeLabel(detail.paymentMode)}` : "No linked order"}
                  </p>
                  <p>{detail.orderStatus || "No linked order"}</p>
                  {detail.orderCreatedAt ? <p>Created {formatDateTime(detail.orderCreatedAt)}</p> : null}
                  <p>{detail.deliveredAt ? `Delivered ${formatDateTime(detail.deliveredAt)}` : "Not delivered yet"}</p>
                  <p>{detail.giftCardTheme?.name || "Theme pending"}</p>
                  {detail.internalNote ? <p>Note: {detail.internalNote}</p> : null}
                </div>
              </div>

              {resendMutation.error ? <p className="error-text">{(resendMutation.error as Error).message}</p> : null}
              {receiptMutation.error ? <p className="error-text">{(receiptMutation.error as Error).message}</p> : null}

              <Card title="Adjust balance" eyebrow="Manual adjustment">
                <form
                  className="stack"
                  onSubmit={adjustmentForm.handleSubmit(async (values) => {
                    await adjustmentMutation.mutateAsync(values);
                  })}
                >
                  <Field label="Amount delta">
                    <input
                      type="number"
                      step="0.01"
                      value={adjustmentAmountDollars}
                      onChange={(event) =>
                        adjustmentForm.setValue("amountDelta", Math.round(Number(event.target.value || 0) * 100), {
                          shouldDirty: true,
                          shouldValidate: true
                        })
                      }
                    />
                  </Field>
                  <Field label="Reason">
                    <input {...adjustmentForm.register("note")} placeholder="Balance correction" />
                  </Field>
                  {adjustmentMutation.error ? <p className="error-text">{(adjustmentMutation.error as Error).message}</p> : null}
                  <Button type="submit" disabled={adjustmentMutation.isPending}>
                    {adjustmentMutation.isPending ? "Saving..." : "Apply adjustment"}
                  </Button>
                </form>
              </Card>

              <div ref={redemptionsSectionRef} className="stack">
                <p className="eyebrow">Redemptions</p>
                {redemptionsQuery.isLoading ? <p className="card-copy">Loading redemptions...</p> : null}
                {redemptionsQuery.error ? <p className="error-text">{(redemptionsQuery.error as Error).message}</p> : null}
                <div className="table-wrap">
                  <table className="table customer-table booking-table">
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th>Service date</th>
                        <th>Total</th>
                        <th>Amount redeemed</th>
                        <th>Reference</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(redemptionsQuery.data?.items ?? []).length ? (
                        (redemptionsQuery.data?.items ?? []).map((redemption) => (
                          <tr key={redemption.id}>
                            <td>
                              <strong>{redemption.customerName || "Legacy redemption"}</strong>
                              <span>{redemption.note || redemption.source}</span>
                            </td>
                            <td>{redemption.serviceDate ? formatDateTime(redemption.serviceDate) : formatDateTime(redemption.createdAt)}</td>
                            <td>{redemption.bookingTotal ? formatCurrency(redemption.bookingTotal) : "-"}</td>
                            <td>{formatCurrency(redemption.amountRedeemed)}</td>
                            <td>{redemption.bookingReference || "-"}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5}>No redemptions recorded for this gift card yet.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div ref={changesSectionRef} className="table-wrap">
                <table className="table customer-table booking-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Reference</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.transactions.length ? (
                      detail.transactions.map((transaction) => (
                        <tr key={transaction.id}>
                          <td>{formatDateTime(transaction.createdAt)}</td>
                          <td>{transaction.transactionType}</td>
                          <td>{transaction.amount > 0 ? formatCurrency(transaction.amount) : `-${formatCurrency(Math.abs(transaction.amount))}`}</td>
                          <td>{transaction.bookingReference || String(transaction.config?.bookingReference ?? "") || "-"}</td>
                          <td>{transaction.note || String(transaction.config?.note ?? "") || "-"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5}>No ledger activity yet.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </Card>
      ) : null}

      {activeTab === "themes" ? (
        <Card
          title={isCreatingTheme ? "Create a theme" : selectedTheme?.name || "Theme details"}
          eyebrow={isCreatingTheme ? "Occasion theme" : selectedTheme?.occasionType || "Gift card theme"}
        >
          <form
            className="stack"
            onSubmit={themeForm.handleSubmit(async (values) => {
              if (isCreatingTheme) {
                await themeCreateMutation.mutateAsync(values);
                return;
              }
              await themeUpdateMutation.mutateAsync(values);
            })}
          >
            <div className="grid two">
              <Field label="Theme name">
                <input {...themeForm.register("name")} placeholder="Birthday" />
              </Field>
              <Field label="Slug">
                <input {...themeForm.register("slug")} placeholder="birthday" />
              </Field>
              <Field label="Occasion type">
                <div className="stack stack--tight">
                  <input list="gift-card-occasion-types" {...themeForm.register("occasionType")} placeholder="general" />
                  <datalist id="gift-card-occasion-types">
                    {GIFT_CARD_OCCASION_OPTIONS.map((occasion) => (
                      <option key={occasion} value={occasion} />
                    ))}
                  </datalist>
                  <p className="card-copy">Use general for an all-purpose card that works for any occasion.</p>
                </div>
              </Field>
              <Field label="Sort order">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={Number(themeForm.watch("displayOrder") ?? 0)}
                  onChange={(event) =>
                    themeForm.setValue("displayOrder", Math.max(0, Number(event.target.value || 0)), {
                      shouldDirty: true,
                      shouldValidate: true
                    })
                  }
                />
              </Field>
              <Field label="Accent color">
                <div className="theme-color-field">
                  <input type="hidden" {...themeForm.register("accentColor")} />
                  <input
                    aria-label="Accent color picker"
                    className="theme-color-field__swatch"
                    type="color"
                    value={accentColorPickerValue}
                    onChange={(event) =>
                      themeForm.setValue("accentColor", event.target.value, {
                        shouldDirty: true,
                        shouldValidate: true
                      })
                    }
                  />
                  <input
                    value={themeDraft.accentColor}
                    onChange={(event) =>
                      themeForm.setValue("accentColor", event.target.value, {
                        shouldDirty: true,
                        shouldValidate: true
                      })
                    }
                    placeholder="#c65f3f"
                  />
                </div>
              </Field>
              <Field label="Text color">
                <div className="theme-color-field">
                  <input type="hidden" {...themeForm.register("textColor")} />
                  <input
                    aria-label="Text color picker"
                    className="theme-color-field__swatch"
                    type="color"
                    value={textColorPickerValue}
                    onChange={(event) =>
                      themeForm.setValue("textColor", event.target.value, {
                        shouldDirty: true,
                        shouldValidate: true
                      })
                    }
                  />
                  <input
                    value={themeDraft.textColor}
                    onChange={(event) =>
                      themeForm.setValue("textColor", event.target.value, {
                        shouldDirty: true,
                        shouldValidate: true
                      })
                    }
                    placeholder="#18261f"
                  />
                </div>
              </Field>
            </div>

            <Field label="Background image URL">
              <input {...themeForm.register("backgroundImageUrl")} placeholder="https://..." />
            </Field>
            <Field label="Headline">
              <input {...themeForm.register("headline")} placeholder="Happy Birthday!" />
            </Field>
            <Field label="Subheadline">
              <textarea {...themeForm.register("subheadline")} rows={3} placeholder="Celebrate with a little sparkle and a lot of care." />
            </Field>

            <div className="grid two">
              <label className="booking-inline-label">
                <input
                  type="checkbox"
                  checked={Boolean(themeForm.watch("active"))}
                  onChange={(event) =>
                    themeForm.setValue("active", event.target.checked, {
                      shouldDirty: true,
                      shouldValidate: true
                    })
                  }
                />
                Active
              </label>
              <label className="booking-inline-label">
                <input
                  type="checkbox"
                  checked={Boolean(themeForm.watch("isDefault"))}
                  onChange={(event) =>
                    themeForm.setValue("isDefault", event.target.checked, {
                      shouldDirty: true,
                      shouldValidate: true
                    })
                  }
                />
                Default theme
              </label>
            </div>

            <div className="stack">
              <p className="eyebrow">Preview</p>
              <GiftCardThemePreview
                theme={themeDraft}
                amount={10000}
                recipientName="Recipient name"
                purchaserName="Purchaser name"
                message="Enjoy a little more breathing room this week."
                caption="This preview is used across purchase, confirmation, admin detail, and email delivery."
              />
            </div>

            {themeCreateMutation.error ? <p className="error-text">{(themeCreateMutation.error as Error).message}</p> : null}
            {themeUpdateMutation.error ? <p className="error-text">{(themeUpdateMutation.error as Error).message}</p> : null}
            {themeStatusMutation.error ? <p className="error-text">{(themeStatusMutation.error as Error).message}</p> : null}

            <div className="hero__actions">
              <Button type="submit" disabled={themeCreateMutation.isPending || themeUpdateMutation.isPending}>
                {themeCreateMutation.isPending || themeUpdateMutation.isPending
                  ? "Saving..."
                  : isCreatingTheme
                    ? "Create theme"
                    : "Save theme"}
              </Button>
              {!isCreatingTheme && selectedTheme ? (
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() =>
                    themeStatusMutation.mutate({
                      active: !selectedTheme.active
                    })
                  }
                  disabled={themeStatusMutation.isPending}
                >
                  {themeStatusMutation.isPending ? "Updating..." : selectedTheme.active ? "Archive theme" : "Activate theme"}
                </Button>
              ) : null}
              {!isCreatingTheme && selectedTheme && !selectedTheme.isDefault ? (
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() =>
                    themeStatusMutation.mutate({
                      active: true,
                      isDefault: true
                    })
                  }
                  disabled={themeStatusMutation.isPending}
                >
                  Make default
                </Button>
              ) : null}
              {isCreatingTheme ? (
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => {
                    setIsCreatingTheme(false);
                    if (selectedTheme) {
                      themeForm.reset(createThemeDraft(selectedTheme));
                    }
                  }}
                >
                  Cancel
                </Button>
              ) : null}
            </div>
          </form>
        </Card>
      ) : null}
    </div>
  );
}
